import React from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion } from 'framer-motion';
import { Droplets, Heart, FlaskConical, Stethoscope, ArrowRight, Lightbulb } from 'lucide-react';
const PROFILES = {
  Nephrologist: {
    icon: Droplets,
    beliefs: ['Late-stage kidney specialist leading care in advanced/high-risk CKD', 'Often manage a more fragile patient population', 'Motivated by clinical trial data and new advances in CKD', "Frustrated by late referrals and end-stage 'fire-fighting'"],
    priorities: ['Slowing CKD progression while managing hyperkalaemia/safety', 'Cautious, gradual treatment influenced by fear of doing harm', 'Hesitant to assume broader management due to high workloads', 'Seek evidence-based direction on treatment sequencing'],
    tension: 'Motivated by evidence but constrained by late referrals and safety concerns'
  },
  Cardiologist: {
    icon: Heart,
    beliefs: ['Focus on CV events and downstream HF/CKD diagnosed patients', "Pride themselves on adopting a proactive, 'aggressive' approach", 'Recognize interplay between heart and kidney health', "Welcome shift from needing to 'balance' treatments to dual-organ protection"],
    priorities: ['Require proven CV benefit in CKD', 'Comfortable with simultaneous or rapid-sequence combination', 'Strongly driven by outcome prevention', 'Less concerned by initial eGFR decline for better overall control'],
    tension: 'Proactive and outcome-driven but may overlook kidney-specific markers'
  },
  Endocrinologist: {
    icon: FlaskConical,
    beliefs: ['Retain patient contact throughout diabetic care', 'Prioritize metabolic control and weight management as preventative', "Cautious to 'over-step' into reactive management of progressive decline", "Lead early CKD escalation but refer for 'non-glycaemic' treatment"],
    priorities: ['Prioritize treatments with metabolic effect, followed by kidney protection', 'Support multi-modal care as an escalation strategy', 'Data-driven specialty, collating biomarker information', 'Routine monitoring of UACR to identify early-stage CKD'],
    tension: 'Preventative mindset but cautious about overstepping into non-metabolic management'
  },
  PCP: {
    icon: Stethoscope,
    beliefs: ['Manage early-stage CKM before referring for specialist care', 'Broader role propelled by comfort with SGLT2 and GLP1', "Tend to stick with 'tried and true' therapies", 'Low confidence in escalating therapies'],
    priorities: ['Holistic treatment approach and focus on prevention', 'Adopt a conservative, step-wise approach to treatment', 'Strong desire for simplified treatment approaches', 'Looking for guidelines and social-proof / peer advocacy'],
    tension: 'Broad patient access but low confidence in escalation'
  }
};
export function SpecialtyDeepDive({
  state,
  nextStep
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const profile = PROFILES[role];
  const Icon = profile.icon;
  const color = SPECIALTY_COLORS[role];
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto" data-id="element-409">
      <div className="max-w-5xl w-full mx-auto" data-id="element-410">
        <div className="flex items-center mb-10" data-id="element-411">
          <div className="p-4 rounded-2xl mr-6" style={{
          backgroundColor: `${color}15`,
          color: color
        }} data-id="element-412">
            <Icon size={40} data-id="element-413" />
          </div>
          <div data-id="element-414">
            <h2 className="text-3xl md:text-4xl font-bold text-dark" data-id="element-415">
              Understanding {role}s
            </h2>
            <p className="text-gray-500 text-lg mt-1" data-id="element-416">
              Their beliefs, priorities, and mindset.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10" data-id="element-417">
          <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100" data-id="element-418">
            <h3 className="text-xl font-bold mb-6 flex items-center" style={{
            color
          }} data-id="element-419">
              <span className="w-8 h-px bg-current mr-4 opacity-50" data-id="element-420"></span>
              What they believe
            </h3>
            <ul className="space-y-4" data-id="element-421">
              {profile.beliefs.map((belief, i) => <li key={i} className="flex items-start" data-id="element-422">
                  <span className="mr-3 mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                backgroundColor: color
              }} data-id="element-423"></span>
                  <span className="text-gray-700 leading-relaxed" data-id="element-424">
                    {belief}
                  </span>
                </li>)}
            </ul>
          </div>

          <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100" data-id="element-425">
            <h3 className="text-xl font-bold mb-6 flex items-center" style={{
            color
          }} data-id="element-426">
              <span className="w-8 h-px bg-current mr-4 opacity-50" data-id="element-427"></span>
              Treatment priorities
            </h3>
            <ul className="space-y-4" data-id="element-428">
              {profile.priorities.map((priority, i) => <li key={i} className="flex items-start" data-id="element-429">
                  <span className="mr-3 mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                backgroundColor: color
              }} data-id="element-430"></span>
                  <span className="text-gray-700 leading-relaxed" data-id="element-431">
                    {priority}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="bg-white border-2 rounded-3xl p-8 shadow-sm flex items-start mb-10" style={{
        borderColor: `${color}30`
      }} data-id="element-432">
          <div className="p-3 rounded-full mr-6 flex-shrink-0" style={{
          backgroundColor: `${color}15`,
          color
        }} data-id="element-433">
            <Lightbulb size={24} data-id="element-434" />
          </div>
          <div data-id="element-435">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-1" style={{
            color
          }} data-id="element-436">
              Key Insight
            </h3>
            <p className="text-xl font-semibold text-dark" data-id="element-437">{profile.tension}</p>
          </div>
        </div>

        <div className="flex justify-center" data-id="element-438">
          <motion.button initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} onClick={nextStep} className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-439">
            Continue
            <ArrowRight className="ml-2 w-5 h-5" data-id="element-440" />
          </motion.button>
        </div>
      </div>
    </div>;
}